import type { TBrandingFragment } from './map-branding';

const ORGANIZATION_BRANDING_SESSION_KEY = 'crehana-org-branding-v1:@session';

const HEX_COLOR = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const PALETTE_KEYS = ['darkest', 'dark', 'base', 'light', 'lightest'] as const;

type TPaletteKey = (typeof PALETTE_KEYS)[number];

type TSessionPalette = Record<TPaletteKey, string>;

type TOrganizationBrandingSession = {
  palette: TSessionPalette;
  favicon?: string;
};

const PALETTE_VARIABLES: Record<TPaletteKey, string[]> = {
  darkest: [
    '--cds-brand-90',
    '--color-primary-light-700',
    '--color-primary-light-600',
    '--color-primary-dark-700',
    '--color-primary-dark',
  ],
  dark: ['--cds-brand-50', '--color-primary-light-500', '--color-primary-main'],
  base: [
    '--cds-brand-40',
    '--color-primary-light-400',
    '--color-primary-dark-600',
    '--color-primary-light',
  ],
  light: [
    '--cds-brand-30',
    '--color-primary-light-300',
    '--color-primary-dark-500',
    '--color-primary-dark-400',
    '--color-primary-dark-300',
    '--color-primary-lighter',
  ],
  lightest: [
    '--cds-brand-10',
    '--color-primary-light-200',
    '--color-primary-light-100',
    '--color-primary-dark-200',
    '--color-primary-dark-100',
  ],
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && HEX_COLOR.test(value);

const parsePalette = (value: unknown): TSessionPalette | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const { darkest, dark, base, light, lightest } = value;

  if (
    !isHexColor(darkest) ||
    !isHexColor(dark) ||
    !isHexColor(base) ||
    !isHexColor(light) ||
    !isHexColor(lightest)
  ) {
    return undefined;
  }

  return { darkest, dark, base, light, lightest };
};

const parseFavicon = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  try {
    const url = new URL(value);

    return url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
};

const parseSession = (
  value: unknown,
): TOrganizationBrandingSession | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const palette = parsePalette(value.palette);

  if (!palette) {
    return undefined;
  }

  const favicon = parseFavicon(value.favicon);

  return favicon ? { palette, favicon } : { palette };
};

const readSession = (): TOrganizationBrandingSession | undefined => {
  try {
    const raw = window.localStorage.getItem(ORGANIZATION_BRANDING_SESSION_KEY);

    if (!raw) {
      return undefined;
    }

    const value: unknown = JSON.parse(raw);

    return parseSession(value);
  } catch {
    return undefined;
  }
};

export const clearOrganizationBrandingSession = (): void => {
  try {
    window.localStorage.removeItem(ORGANIZATION_BRANDING_SESSION_KEY);
  } catch {
    return;
  }
};

export const saveOrganizationBrandingSession = (
  branding: TBrandingFragment,
): void => {
  const session = parseSession({
    palette: branding.palette_colors,
    favicon: branding.image_favicon.absolute_url,
  });

  if (!session) {
    clearOrganizationBrandingSession();

    return;
  }

  try {
    window.localStorage.setItem(
      ORGANIZATION_BRANDING_SESSION_KEY,
      JSON.stringify(session),
    );
  } catch {
    return;
  }
};

const ICON_LINKS = 'link[rel~="icon"]';

let sessionGuard: MutationObserver | undefined;

const isSessionOnPage = ({
  palette,
  favicon,
}: TOrganizationBrandingSession): boolean => {
  const { style } = document.documentElement;

  const hasPalette = PALETTE_KEYS.every(key =>
    PALETTE_VARIABLES[key].every(
      variable => style.getPropertyValue(variable) === palette[key],
    ),
  );

  if (!hasPalette || !favicon) {
    return hasPalette;
  }

  return Array.from(
    document.querySelectorAll<HTMLLinkElement>(ICON_LINKS),
  ).every(link => link.href === favicon);
};

const writeSessionOnPage = ({
  palette,
  favicon,
}: TOrganizationBrandingSession): void => {
  const root = document.documentElement;

  for (const key of PALETTE_KEYS) {
    for (const variable of PALETTE_VARIABLES[key]) {
      root.style.setProperty(variable, palette[key]);
    }
  }

  if (!favicon) {
    return;
  }

  for (const link of document.querySelectorAll<HTMLLinkElement>(ICON_LINKS)) {
    link.removeAttribute('type');
    link.href = favicon;
  }
};

export const releaseOrganizationBrandingSession = (): boolean => {
  if (!sessionGuard) {
    return false;
  }

  sessionGuard.disconnect();
  sessionGuard = undefined;

  return true;
};

export const applyOrganizationBrandingSession = (): void => {
  const session = readSession();

  if (!session) {
    return;
  }

  writeSessionOnPage(session);

  if (sessionGuard) {
    return;
  }

  sessionGuard = new MutationObserver(() => {
    if (!isSessionOnPage(session)) {
      writeSessionOnPage(session);
    }
  });
  sessionGuard.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style'],
  });
  sessionGuard.observe(document.head, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href'],
  });
};
